import { Component, Inject, OnInit, OnDestroy, TemplateRef } from '@angular/core';
import { Device, MediaPipelineProcessInfo, MediaPipelineInfo, Scoutview, MediaProcessInfo, ImageDevice } from '@models';
import { MediaPipelineService } from '@services/mediaPipeline.service';
import { ScoutviewService } from '@services/scoutview.service';
import { UserService } from '@services/user.service';
import { AppComponent } from 'app/app.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { KIG_SETTINGS, KigSettings } from 'settings/kig-settings';
import { DragulaService } from 'ng2-dragula';
import { ChangeLogNotesParams, ChangeLogNotesComponent } from 'app/components/messages/changelog-notes/changelognotes.components';

export interface MediaPipelineParams {
  selectedImageDevice: ImageDevice;
  imageDevices: ImageDevice[];
  scoutviews: Scoutview[];
  mediaPipelineInfo: MediaPipelineInfo;
  createOrUpdateTag: string;
}

@Component({
  standalone: false,
  selector: 'media-pipeline',
  templateUrl: './media-pipeline.component.html',
  styleUrls: ['./media-pipeline.component.scss']
})

export class MediaPipelineComponent implements OnInit, OnDestroy, MediaPipelineParams {

  modalDragOn: boolean = false;

  scoutviews: Scoutview[];
  imageDevices: ImageDevice[] = [];
  selectedImageDevice: ImageDevice;
  mediaPipelineInfo: MediaPipelineInfo;
  createOrUpdateTag: string;

  pipelineLoaded: boolean = true;
  username: string = "unknown";
  changeLogNotes = '';

  logModalRef: BsModalRef;
  pipelineModalRef: BsModalRef;

  availableMediaProcessingSteps: MediaProcessInfo[] = [];
  selectedMediaProcessingSteps: MediaProcessInfo[] = [];

  // The search box in the title bar of "All Available Media Processing Steps". This array is exactly the rows shown
  // in that table, in order, and is also the drag source's model (dragulaModel) - dragula finds the dragged step by
  // the row's position, so the two must always match. It is replaced (never edited in place or rebuilt by a getter)
  // only when the search text or the steps change.
  stepSearchText: string = '';
  filteredAvailableSteps: MediaProcessInfo[] = [];

  public MediaProcessingCols = [
    { field: 'add', header: '', width: '4%' },        // the "+" that adds the step to the pipeline
    { field: 'id', header: 'Id', width: '4%' },
    { field: 'name', header: 'Name', width: '14%' },
    { field: 'description', header: 'Description', width: '33%' },
    { field: 'inputType', header: 'Input', width: '10%' },
    { field: 'outputType', header: 'Output', width: '10%' },
    { field: 'parameters', header: 'Parameters', width: '25%' }
  ];

  constructor(public bsModalRef: BsModalRef,
    private toastr: ToastrService,
    private modalService: BsModalService,
    public global: AppComponent,
    private confirmationService: ConfirmationService,
    @Inject(KIG_SETTINGS) private settings: KigSettings,
    private _scoutviewService: ScoutviewService,
    public _userService: UserService,
    public _mediaPipelineService: MediaPipelineService,
    private _dragulaService: DragulaService) {

    this._dragulaService.createGroup('pipeline-steps', {
      copy: (el, source) => source.id === 'sourceBag',
      copyItem: (item: MediaProcessInfo) => { const newItem: MediaProcessInfo = this.clone(item); return newItem; },
      accepts: (el, target, source, sibling) => target.id === 'destinationBag',
      // Pressing on an element marked "no-drag" (the "+" cell) never starts a drag, so a click there only adds the step.
      invalid: (el) => el?.classList?.contains('no-drag') ?? false,
      removeOnSpill: true
    });
  }


  ngOnInit(): void {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {

        this.username = results['value'];
        if (this.username != null && this.username != '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }

  
  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  ngOnDestroy(): void {
    this._dragulaService.destroy('pipeline-steps');
  }


  startUp() {
    this.getMediaProcesses();
  }


  getMediaProcesses() {
    this._mediaPipelineService.getMediaProcesses().subscribe(
      results => {
        this.availableMediaProcessingSteps = results;
        this.availableMediaProcessingSteps.forEach(x => {
          x.parametersAsText = this.buildTextFromPipelineProcessParameters(x.parameters);
        });
        this.applyStepFilter();

        this.selectedMediaProcessingSteps =this.mediaPipelineInfo.mediaPipelineProcesses.sort((a, b) => a.position > b.position ? 1 : -1).map(p => new MediaProcessInfo(p, this.availableMediaProcessingSteps));
      },
      err => {
        this.toastr.error('Error unable to get available pipeline processes');
      });
  }


  // Show only the steps that contain the search text in their id, name, description, input / output type or
  // parameters (case-insensitive); an empty search shows every step.
  applyStepFilter() {
    const text = this.stepSearchText.trim().toLowerCase();

    this.filteredAvailableSteps = (text === '')
      ? [...this.availableMediaProcessingSteps]
      : this.availableMediaProcessingSteps.filter(step =>
          [step.id, step.name, step.description, step.inputType, step.outputType, step.parametersAsText]
            .some(value => (value ?? '').toString().toLowerCase().includes(text)));
  }


  // The "+" in the list of available steps: adds a copy of the step to the end of the pipeline, the same as
  // dragging it there (the dragula group copies with clone() too). A new array makes the list redraw; dragula
  // follows it through the [(dragulaModel)] binding, so dragging a step out or re-ordering still works.
  addStepToPipeline(step: MediaProcessInfo) {
    this.selectedMediaProcessingSteps = [...this.selectedMediaProcessingSteps, this.clone(step)];

    // The pipeline sits above the list of steps, so make sure the new step is visible after it renders.
    setTimeout(() => document.getElementById('destinationBag')?.lastElementChild?.scrollIntoView({ block: 'nearest' }));
  }


  addNotes2ChangeLog() {
    this.changeLogNotes = '';
    
    const params: ChangeLogNotesParams = {
      changeLogNotes: this.changeLogNotes,
      userGroupId: 0,
      category: ''
    };

    var modalRef = this.modalService.show(ChangeLogNotesComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, class: 'modal-above-confirmdialog', initialState: params });
    modalRef.content.onChangeLogNotes.subscribe((value) => {
      this.changeLogNotes = value;
    });
  }

  
  updatePipeline(modalRef: BsModalRef) {
    this.updateSelectedMediaSource(this.selectedImageDevice);
    this.buildPipelineProcessesFromSelection(this.mediaPipelineInfo);

    this._mediaPipelineService.createOrUpdateMediaPipeline(this.mediaPipelineInfo).subscribe(
      results => {
        this.toastr.success('Pipeline updated');
        modalRef.hide();
      },
      err => {
        this.toastr.error('Error unable to update pipeline');
      });
  }


  updateSelectedMediaSource(selectedImageDevice: ImageDevice) {
    this.selectedImageDevice = this.imageDevices.find(x => x.name == selectedImageDevice.name);
    this.mediaPipelineInfo.sourceName = this.selectedImageDevice.name;
    this.mediaPipelineInfo.sourceSerial = this.selectedImageDevice.serialNumber;
  }


  updateMediaPipelineActive(value: any) {
    this.mediaPipelineInfo.isActive = !value.checked;
  }


  buildPipelineProcessesFromSelection(pipelineInfo: MediaPipelineInfo) {
    pipelineInfo.mediaPipelineProcesses = [];

    this.selectedMediaProcessingSteps.forEach((step, i) => {
      const pipelineProcess: MediaPipelineProcessInfo = {
        id: 0,
        mediaPipelineId: pipelineInfo.id,
        mediaProcessId: step.id,
        isActive: true,
        parentProcessId: 0,
        childProcessId: 0,
        parameters: step.parameters,
        isComplete: false,
        lastRunDate: new Date(),
        processResults: '',
        position: i
      };
      pipelineInfo.mediaPipelineProcesses.push(pipelineProcess);
    });
  }

  buildTextFromPipelineProcessParameters(parameters: { [key: string]: string }): string {
    let paramArray = [];

    for (let key in parameters) {
      let value = parameters[key];
      paramArray.push(`${key}=<${value}>`);
    }

    return paramArray.join(', ');
  }

  clone(item: MediaProcessInfo): MediaProcessInfo {
    return JSON.parse(JSON.stringify(item));
  }

}
